/**
 * Pure helpers for the Banners admin page. Kept in a sibling module
 * so the unit-test layer can exercise the lifecycle / reach maths
 * without instantiating the React tree.
 */

import {
  BannerRoleT,
  BannerSeverityT,
  BannerT,
  CustomerGroupT,
} from 'types/global'

export type BannerLifecycleStatusT =
  | 'scheduled'
  | 'active'
  | 'ended'
  | 'soft-deleted'

/**
 * Derives the lifecycle badge from `starts_at` / `ends_at` / the
 * soft-delete stamp. Mirrors the CMAPI's view of "active" — a
 * banner is active when `starts_at <= now < ends_at` (or
 * `ends_at` is null, in which case it stays active indefinitely).
 */
export const deriveBannerStatus = (
  banner: Pick<BannerT, 'starts_at' | 'ends_at' | 'deleted_at'>,
  now: Date = new Date(),
): BannerLifecycleStatusT => {
  if (banner.deleted_at) return 'soft-deleted'

  const starts = new Date(banner.starts_at)
  if (Number.isNaN(starts.getTime())) {
    // Defensive — should never happen for a row that round-tripped
    // through the API validators, but the helper shouldn't blow up
    // if the wire format ever drifts.
    return 'scheduled'
  }

  if (starts > now) return 'scheduled'

  if (banner.ends_at) {
    const ends = new Date(banner.ends_at)
    if (!Number.isNaN(ends.getTime()) && ends <= now) return 'ended'
  }

  return 'active'
}

/**
 * Deduped reach across (specific customer IDs) ∪ (every customer in
 * every selected group). Used by the audience selector's live
 * preview and the publish-confirmation modal. Roles can't be
 * counted at compose time — we just append "+ role-matched users"
 * to the rendered phrase when at least one role is picked.
 */
export const computeDedupedReach = (
  selectedCustomerIds: number[],
  selectedGroupIds: string[],
  allGroups: Array<Pick<CustomerGroupT, 'id' | 'customer_ids'>>,
): number => {
  const unique = new Set<number>()
  for (const id of selectedCustomerIds) unique.add(id)
  for (const groupId of selectedGroupIds) {
    const group = allGroups.find((g) => g.id === groupId)
    if (!group) continue
    for (const id of group.customer_ids) unique.add(id)
  }
  return unique.size
}

/**
 * MUI palette colour for the severity chip. Matches MUI's standard
 * Alert/Snackbar variants so the dashboard reads consistently.
 */
export const severityChipColor = (
  severity: BannerSeverityT,
): 'info' | 'warning' | 'error' | 'success' => severity

/**
 * Human-readable role label used in the audience pill / chip.
 */
export const roleLabel = (role: BannerRoleT): string => {
  if (role === 'admin') return 'Admins'
  if (role === 'user') return 'Standard users'
  return role
}

/**
 * Renders the role-filter qualifier as a parenthesised suffix —
 * "(Admins only)" / "(Standard users only)" / "(Admins, Standard
 * users only)". Returns an empty string when no roles are selected,
 * so the caller can append unconditionally without worrying about
 * trailing whitespace.
 *
 * Under the new semantics the role filter NARROWS the customer
 * audience rather than living as an independent OR-axis, so it
 * always reads as a qualifier of the audience set rather than as
 * a peer segment.
 */
export const summariseRoleFilter = (roles: BannerRoleT[]): string => {
  if (roles.length === 0) return ''
  return ` (${roles.map(roleLabel).join(', ')} only)`
}

/**
 * Composes the short audience pill shown in the list row. The
 * caller is responsible for hydrating group names — we receive
 * them as a lookup so the helper stays pure.
 *
 * Roles are no longer treated as a peer audience segment — they
 * narrow the customer audience and render as a parenthesised
 * "(Admins only)" suffix on the pill.
 */
export const formatAudiencePill = (
  banner: Pick<
    BannerT,
    'target_all' | 'target_group_ids' | 'target_customer_ids' | 'target_roles'
  >,
  groupNameById: Map<string, string>,
): string => {
  const roleSuffix = summariseRoleFilter(banner.target_roles)

  if (banner.target_all) return `All customers${roleSuffix}`

  const segments: string[] = []

  for (const groupId of banner.target_group_ids) {
    const name = groupNameById.get(groupId) ?? `Group ${groupId.slice(0, 6)}`
    segments.push(name)
  }

  for (const customerId of banner.target_customer_ids) {
    segments.push(`Customer ${customerId}`)
  }

  if (segments.length === 0) return 'No audience'

  let base: string
  if (segments.length === 1) base = segments[0]
  else if (segments.length === 2) base = segments.join(' + ')
  else base = `${segments[0]} + ${segments.length - 1} more`

  return `${base}${roleSuffix}`
}

/**
 * Returns the locale tab labels in display order. The app itself
 * only renders English in the dashboard chrome; the per-locale
 * tabs here are pure form-state slots, never wired into the
 * i18n provider.
 *
 * All four locales are required for the message body — the dashboard
 * does NOT fall back to English when a locale field is missing, so
 * every banner ships with DA / EN / NB / SV translations.
 */
export const localeTabs = [
  { code: 'en' as const, label: 'English' },
  { code: 'da' as const, label: 'Danish' },
  { code: 'nb' as const, label: 'Norwegian' },
  { code: 'sv' as const, label: 'Swedish' },
]

/**
 * Structural validity check for a CTA URL, shared by the single
 * "same URL for all languages" field and the four per-language
 * fields (ENG2-2106). An empty/whitespace value is NOT invalid —
 * emptiness is a *completeness* concern handled separately by the
 * form, so this helper only answers "is this non-empty text a URL
 * we would put behind a customer-facing button?".
 *
 * We lean on the browser's `URL` constructor for the structural
 * parse, then add two guards of our own:
 *   - protocol must be http/https (the constructor happily accepts
 *     mailto:, javascript:, ftp:, …);
 *   - hostname must contain a dot — single-label hosts like
 *     `https://risika` or `https://localhost` are technically valid
 *     URLs but never what an operator means for a customer CTA.
 *     Bracketed IPv6 still passes (`[::1]` has `:`, not `.`); bare
 *     IPv4 passes because of the dots, which is acceptable since
 *     explicitly typing an IP is intentional.
 */
export const isInvalidCtaUrl = (value: string): boolean => {
  const trimmed = value.trim()
  if (trimmed === '') return false
  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return true
  }
  const protocolOk = parsed.protocol === 'http:' || parsed.protocol === 'https:'
  const hostnameOk = parsed.hostname.includes('.')
  return !(protocolOk && hostnameOk)
}

/**
 * Default `ends_at` = `starts_at + 14 days`, returned as an ISO
 * string so the date picker can hydrate it without timezone drift.
 */
export const defaultEndsAtFromStartsAt = (startsAtIso: string): string => {
  const start = new Date(startsAtIso)
  if (Number.isNaN(start.getTime())) return ''
  const end = new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000)
  return end.toISOString()
}

/**
 * Renders the dedup-reach phrase shown in the live preview and the
 * publish-confirmation modal.
 *
 * Under the new AND-joined semantics the role filter narrows the
 * audience set rather than expanding it, so we describe the
 * audience first and append a "(Admins only)" parenthetical when
 * roles are picked. When `isAllCustomers` is true we skip the
 * numeric reach (the API has it; the dashboard doesn't compute
 * "all customers" against the role filter).
 *
 * Examples:
 *   formatReachPhrase(0, [], true)            → "all customers"
 *   formatReachPhrase(0, ['admin'], true)     → "all customers (Admins only) — exact count is role-dependent"
 *   formatReachPhrase(1, [], false)           → "~1 unique customer"
 *   formatReachPhrase(1, ['admin'], false)    → "~1 unique customer (Admins only) — exact count is role-dependent"
 */
export const formatReachPhrase = (
  reach: number,
  roles: BannerRoleT[],
  isAllCustomers = false,
): string => {
  const hasRoles = roles.length > 0
  const roleSuffix = summariseRoleFilter(roles)
  const roleQualifier = hasRoles ? ' — exact count is role-dependent' : ''

  if (isAllCustomers) {
    return `all customers${roleSuffix}${roleQualifier}`
  }

  const base = `~${reach} unique ${reach === 1 ? 'customer' : 'customers'}`
  return `${base}${roleSuffix}${roleQualifier}`
}
